import type { Point } from '@view-model/models/common/basic';
import { Angle } from '@view-model/models/common/basic';
import { Rect } from '@view-model/models/common/basic/Rect';
import { LineEdge } from '@model-framework/link/LineEdge/LineEdge';

/**
 * 2次のベジェ曲線クラス
 */
export class BezierLine {
  /**
   * コンストラクタ
   * @param p1 始点
   * @param p2 コントロールポイント
   * @param p3 終点
   */
  constructor(
    private readonly p1: Point,
    private readonly p2: Point,
    private readonly p3: Point
  ) {}

  /**
   * 指定されたパラメータt（0〜1）のベジェ曲線上の座標を返します。
   * @param t
   */
  getPoint(t: number): Point {
    const { p1, p2, p3 } = this;

    const q1 = p1.interpolated(p2, t);
    const q2 = p2.interpolated(p3, t);

    return q1.interpolated(q2, t);
  }

  startEdge(): LineEdge {
    const { p1, p2 } = this;

    // コントロールポイントからみたときの始点への向きが矢印の向き
    return new LineEdge(p1, Angle.angleBetween(p2, p1));
  }

  endEdge(): LineEdge {
    const { p2, p3 } = this;

    // コントロールポイントからみたときの終点への向きが矢印の向き
    return new LineEdge(p3, Angle.angleBetween(p2, p3));
  }

  /**
   * このベジェ曲線を包含する矩形を返します。
   */
  boundingRect(): Rect {
    const [p1, p2, ...rest] = this.toPoints();

    return Rect.fromPoints(p1, p2, ...rest);
  }

  /**
   * 始点から終点まで一定の距離閾値で区切ったベジェ曲線上の点を返します。
   * @private
   */
  private toPoints(): Point[] {
    const { p1, p2, p3 } = this;

    return BezierLine.toPointsRecursive(p1, p2, p3).concat(p3);
  }

  private static toPointsRecursive(p1: Point, p2: Point, p3: Point): Point[] {
    if (p1.distanceSquared(p3) < 10.0) return [p1];

    const p12 = p1.interpolated(p2, 0.5);
    const p23 = p2.interpolated(p3, 0.5);
    const mid = p12.interpolated(p23, 0.5);

    return this.toPointsRecursive(p1, p12, mid).concat(this.toPointsRecursive(mid, p23, p3));
  }

  /**
   * この2次ベジェ曲線を表現する、SVGの<path>要素d属性の値を返します。
   * https://developer.mozilla.org/ja/docs/Web/SVG/Element/path
   */
  getSVGPath(): string {
    const { p1, p2, p3 } = this;

    return `M${p1.toSVGPoint()} Q${p2.toSVGPoint()} ${p3.toSVGPoint()}`;
  }

  intersects(rect: Rect): boolean {
    const { p1, p2, p3 } = this;

    return rect.includePosition(p1) || rect.includePosition(p3) || BezierLine.intersectsRecursive(p1, p2, p3, rect);
  }

  /**
   * Rect の境界線とこのベジエ曲線の交点を返す
   */
  getIntersectPoint(rect: Rect): Point | null {
    const { p1, p2, p3 } = this;

    return BezierLine.getIntersectPointRecursive(p1, p2, p3, rect);
  }

  private static intersectsRecursive(p1: Point, p2: Point, p3: Point, rect: Rect): boolean {
    // 始点終点が十分に近づいた場合はそこで探索を打ち切る
    if (p1.distanceSquared(p3) < 1.0) {
      return false;
    }

    // 曲線の中点を求めて当たり判定をする
    const q1 = p1.interpolated(p2, 0.5);
    const q2 = p2.interpolated(p3, 0.5);
    const q3 = q1.interpolated(q2, 0.5);

    if (rect.includePosition(q3)) return true;

    // ヒットしていなければ曲線を2分割して再起的に当たり判定をする
    return BezierLine.intersectsRecursive(p1, q1, q3, rect) || BezierLine.intersectsRecursive(q3, q2, p3, rect);
  }

  private static getIntersectPointRecursive(p1: Point, p2: Point, p3: Point, rect: Rect): Point | null {
    // ベジエ曲線の始点・終点・制御点を含む矩形が rect と交差しない場合には、交点なしと判断して探索を打ち切る
    if (!Rect.fromPoints(p1, p2, p3).intersects(rect)) {
      return null;
    }

    // ベジエ曲線の始点・終点・制御点がすべて rect の内側にある場合には、交点なしと判断して探索を打ち切る
    if (rect.innerInclude(p1) && rect.innerInclude(p2) && rect.innerInclude(p3)) {
      return null;
    }

    // 始点が一定の範囲内で rect の境界線上にある場合には、その点を交点として返す
    if (BezierLine.isOnRectBoundary(p1, rect)) {
      return p1;
    }

    // 始点終点が十分に近づいた場合はそこで探索を打ち切る
    if (p1.distanceSquared(p3) < 1.0) {
      return BezierLine.getSegmentRectBoundaryIntersection(p1, p3, rect);
    }

    // 曲線の中点を求めて当たり判定をする
    const q1 = p1.interpolated(p2, 0.5);
    const q2 = p2.interpolated(p3, 0.5);
    const q3 = q1.interpolated(q2, 0.5);

    // 曲線を2分割し、始点側から順に探索して最初の交点を返す
    return (
      BezierLine.getIntersectPointRecursive(p1, q1, q3, rect) || BezierLine.getIntersectPointRecursive(q3, q2, p3, rect)
    );
  }

  /**
   * 線分 start-end と Rect の境界線の交点を返します。
   * 交点が複数ある場合は、start から最も近い交点（最小 t）を返します。
   */
  private static getSegmentRectBoundaryIntersection(start: Point, end: Point, rect: Rect): Point | null {
    const epsilon = 1e-6;
    const { left, top, right, bottom } = rect.getLTRB();
    const dx = end.x - start.x;
    const dy = end.y - start.y;

    // 線分が実質1点に潰れている場合は、その点が境界上かどうかだけを判定する
    if (Math.abs(dx) < epsilon && Math.abs(dy) < epsilon) {
      return BezierLine.isOnRectBoundary(start, rect) ? start : null;
    }

    // t は start(0) -> end(1) の線形補間パラメータ。
    // 各辺との交点候補を集め、最後に最小 t を選ぶ。
    const candidates: Array<{ t: number; point: Point }> = [];
    const addCandidate = (t: number, point: Point): void => {
      // 線分範囲外（t<0 or t>1）は採用しない
      if (t < -epsilon || t > 1 + epsilon) return;

      const normalizedT = Math.max(0, Math.min(1, t));
      // 角付近で同一点が重複登録されるのを防ぐ
      if (candidates.some((candidate) => Math.abs(candidate.t - normalizedT) < epsilon)) return;

      candidates.push({ t: normalizedT, point });
    };

    // 始点が境界上ならそれ自体を候補に入れる
    if (BezierLine.isOnRectBoundary(start, rect)) {
      addCandidate(0, start);
    }

    // 垂直辺（x=left/right）との交差判定
    if (Math.abs(dx) >= epsilon) {
      const tLeft = (left - start.x) / dx;
      const yLeft = start.y + tLeft * dy;
      if (yLeft >= top - epsilon && yLeft <= bottom + epsilon) {
        addCandidate(tLeft, start.interpolated(end, tLeft));
      }

      const tRight = (right - start.x) / dx;
      const yRight = start.y + tRight * dy;
      if (yRight >= top - epsilon && yRight <= bottom + epsilon) {
        addCandidate(tRight, start.interpolated(end, tRight));
      }
    }

    // 水平辺（y=top/bottom）との交差判定
    if (Math.abs(dy) >= epsilon) {
      const tTop = (top - start.y) / dy;
      const xTop = start.x + tTop * dx;
      if (xTop >= left - epsilon && xTop <= right + epsilon) {
        addCandidate(tTop, start.interpolated(end, tTop));
      }

      const tBottom = (bottom - start.y) / dy;
      const xBottom = start.x + tBottom * dx;
      if (xBottom >= left - epsilon && xBottom <= right + epsilon) {
        addCandidate(tBottom, start.interpolated(end, tBottom));
      }
    }

    // 候補がなければ境界線との交点なし
    if (candidates.length === 0) return null;

    // 始点側（最小 t）の交点を返す
    candidates.sort((a, b) => a.t - b.t);
    return candidates[0].point;
  }

  private static isOnRectBoundary(point: Point, rect: Rect): boolean {
    const epsilon = 1e-6;
    const { left, top, right, bottom } = rect.getLTRB();

    const onLeft = Math.abs(point.x - left) < epsilon && point.y >= top - epsilon && point.y <= bottom + epsilon;
    const onRight = Math.abs(point.x - right) < epsilon && point.y >= top - epsilon && point.y <= bottom + epsilon;
    const onTop = Math.abs(point.y - top) < epsilon && point.x >= left - epsilon && point.x <= right + epsilon;
    const onBottom = Math.abs(point.y - bottom) < epsilon && point.x >= left - epsilon && point.x <= right + epsilon;

    return onLeft || onRight || onTop || onBottom;
  }
}
